import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../icons';

const norm = (s) => s.trim().toLowerCase();
const CREATE = Symbol('create');

// Menu a tendina con filtro live: mostra le opzioni esistenti mentre si scrive
// e propone "Crea "<testo>"" quando il valore digitato non corrisponde a
// nessuna opzione, cosi' si puo' scegliere o creare un nuovo valore senza
// mai lasciare il form (tastiera: frecce, Invio, Esc).
//
// L'elenco e' renderizzato in un portal su document.body: le card del
// layout usano backdrop-filter, che crea un proprio stacking context e
// "intrappolerebbe" un dropdown assoluto sotto la card successiva.
function Combobox({ id, value, onChange, options, placeholder, required }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const inputRef = useRef(null);
  const listboxId = `${id}-listbox`;

  useLayoutEffect(() => {
    if (!open) return;
    const updateRect = () => {
      const el = inputRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect({ top: r.bottom + 6, left: r.left, width: r.width });
    };
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [open]);

  const uniqueOptions = useMemo(
    () => Array.from(new Set((options || []).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'it', { sensitivity: 'base' })),
    [options]
  );

  const filtered = useMemo(() => {
    const q = norm(query);
    return q ? uniqueOptions.filter((o) => norm(o).includes(q)) : uniqueOptions;
  }, [uniqueOptions, query]);

  const trimmed = query.trim();
  const exactMatch = uniqueOptions.some((o) => norm(o) === norm(trimmed));
  const showCreate = trimmed !== '' && !exactMatch;
  const rows = showCreate ? [...filtered, { type: CREATE, label: trimmed }] : filtered;

  const commit = (label) => {
    const v = label.trim();
    onChange(v);
    setQuery(v);
    setOpen(false);
    setActiveIndex(0);
  };

  const openList = () => { setQuery(''); setOpen(true); setActiveIndex(0); };

  const handleChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    setOpen(true);
    setActiveIndex(0);
  };

  const handleKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) { e.preventDefault(); openList(); return; }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (rows.length ? (i + 1) % rows.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (rows.length ? (i - 1 + rows.length) % rows.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const row = rows[activeIndex];
      if (row) commit(row.type === CREATE ? row.label : row);
      else if (trimmed) commit(trimmed);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setQuery(value || '');
      setOpen(false);
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  const handleBlur = () => {
    if (!open) return;
    if (trimmed === '') { setQuery(value || ''); setOpen(false); return; }
    if (norm(trimmed) === norm(value || '')) { setQuery(value || ''); setOpen(false); return; }
    commit(trimmed);
  };

  const handleClear = () => {
    onChange('');
    setQuery('');
    inputRef.current?.focus();
  };

  return (
    <div className={`combobox${open ? ' is-open' : ''}`}>
      <div className="combobox-control">
        <input
          id={id}
          ref={inputRef}
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={open && rows.length ? `${id}-opt-${activeIndex}` : undefined}
          className="input combobox-input"
          autoComplete="off"
          value={open ? query : (value || '')}
          placeholder={placeholder}
          required={required}
          onFocus={openList}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
        />
        {!open && value && (
          <button type="button" className="combobox-clear" aria-label="Cancella selezione" tabIndex={-1} onMouseDown={(e) => e.preventDefault()} onClick={handleClear}>
            <Icon name="x" size={13} />
          </button>
        )}
        <Icon name="chevronRight" size={15} className="combobox-caret" />
      </div>
      {open && rect && createPortal(
        <ul className="combobox-list" role="listbox" id={listboxId} style={{ top: rect.top, left: rect.left, minWidth: rect.width }}>
          {rows.length === 0 && <li className="combobox-empty">Digita per creare un nuovo valore</li>}
          {rows.map((row, i) => {
            const isCreate = row && row.type === CREATE;
            const label = isCreate ? row.label : row;
            const selected = !isCreate && norm(label) === norm(value || '');
            return (
              <li key={isCreate ? '__create__' : label} role="option" id={`${id}-opt-${i}`} aria-selected={i === activeIndex}>
                <button
                  type="button"
                  className={`combobox-option${i === activeIndex ? ' is-active' : ''}${isCreate ? ' is-create' : ''}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => commit(label)}
                >
                  {isCreate ? (
                    <><Icon name="plus" size={14} />Crea &ldquo;{label}&rdquo;</>
                  ) : (
                    <><Icon name="check" size={14} className={`combobox-check${selected ? '' : ' is-hidden'}`} /><span>{label}</span></>
                  )}
                </button>
              </li>
            );
          })}
        </ul>,
        document.body
      )}
    </div>
  );
}

export default Combobox;
